import React, { useEffect, useMemo, useRef } from 'react';
import { mdiChevronDown, mdiChevronUp, mdiSortAlphabeticalAscending, mdiSortAlphabeticalDescending, mdiSortCalendarAscending, mdiSortCalendarDescending, mdiBellOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Add, AssessmentOutlined, DashboardOutlined } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PublisherIcon from '@mui/icons-material/Newspaper';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import { Box, List } from '@mui/material';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import Constants from 'relevant-shared/reportData/constants';
import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { usePinnedReports } from '../../hooks/usePinnedReports';
import Base from '../../layouts/Base';
import { PRODUCT_TYPE } from '../../lib/access';
import SystemData from '../../lib/systemData';
import { stores } from '../../stores';
import { useSidebarStore } from '../../stores/SidebarStore';
import AlertBalloon from '../AlertBalloon/AlertBalloon';
import { IconButton } from '../Button/IconButton';
import { Tooltip } from '../Tooltip';
import { adminHbManagementMenuItems, adminMenuItems, adminSettingsMenuItems, SECTION } from './config';
import useExpandedSections from './hooks/useExpandedSections';
import SidebarButton from './SidebarButton';
import { ReactComponent as KidStarFilled } from '../../assets/icons/kid_star_filled.svg?component';
import { Drawer } from '../Accordion/Accordion';
import { useReportsOverviewStore } from '../ReportsOverview/reportsOverviewStore';

const pinnedReportsSortOptions = {
	az: { value: 'az', label: 'A-Z', icon: mdiSortAlphabeticalAscending, tooltip: 'Alphabetical (A-Z)', fn: (a, b) => a.settings.title.localeCompare(b.settings.title) },
	za: { value: 'za', label: 'Z-A', icon: mdiSortAlphabeticalDescending, tooltip: 'Alphabetical (Z-A)', fn: (a, b) => b.settings.title.localeCompare(a.settings.title) },
	newest: { value: 'newest', label: 'Newest first', icon: mdiSortCalendarAscending, tooltip: 'Newest first', fn: (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime() },
	oldest: { value: 'oldest', label: 'Oldest first', icon: mdiSortCalendarDescending, tooltip: 'Oldest first', fn: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime() },
};

export function Sidebar({ productType }: { productType: string }) {
	const { pathname, search } = useLocation();
	const [expandedSections, updateExpandedSections] = useExpandedSections();
	const pinnedReports = usePinnedReports();
	const {
		mobileOpen,
		setMobileOpen,
		pinnedReportsSort,
		setPinnedReportsSort,
		favouritesCollapsed,
		setFavouritesCollapsed,
	} = useSidebarStore();
	const maxSm = useBreakpoint('max-sm');
	const currentPath = `${pathname}${search}`;

	// Close mobile sidebar when the breakpoint changes
	useEffect(() => {
		if (!maxSm) {
			setMobileOpen(false);
		}
	}, [maxSm, setMobileOpen]);

	const {
		systemSettings,
		hasGlobalAdminAccess,
		hbmEnabled,
	} = SystemData.genericData;

	const {
		hide,
	} = systemSettings;

	const user = stores.identity.value;

	const canEditUsers = (user && user.profile && user.profile.permissions && user.profile.permissions.canEditUsers);

	const hideInvoices = hide.invoices
		&& (productType === PRODUCT_TYPE.FULL
			|| productType === PRODUCT_TYPE.PROGRAMMATIC
			|| productType === PRODUCT_TYPE.AUDIENCE);

	const handlePinnedSort = () => {
		const options = Object.keys(pinnedReportsSortOptions);
		const idx = options.findIndex((key) => key === pinnedReportsSort);
		setPinnedReportsSort(options[(idx + 1) % options.length]);
	};

	const pinnedReportsSorted = useMemo(() => {
		return pinnedReports?.sort((a, b) => pinnedReportsSortOptions[pinnedReportsSort].fn(a, b));
	}, [pinnedReports, pinnedReportsSort]);

	const navRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const handleScroll = () => {
			// As the page scrolls down, we need to grow the nav element height to account
			// for the top bar disappearing and the sticky nav element moving up.
			// Otherwise, you are not able to scroll the nav element fully. It's just
			// an unfortunate side effect of how position: sticky works.
			const offset = window.scrollY;
			navRef.current?.style.setProperty('--nav-offset', `${offset}px`);
		};
		window.addEventListener('scroll', handleScroll);
		return () => { window.removeEventListener('scroll', handleScroll); };
	}, []);

	const resetReportsOverview = useReportsOverviewStore((store) => store.reset);

	return (
		<div className={clsx(
			'@container flex-none bg-component-navbar-bg-primary',
			// Sidebar shrinks to 110px on sm, and expands to 230px on lg
			'sm:basis-(--sidebar-sm) lg:basis-(--sidebar-lg)',
			// Full width sidebar on mobile, through hamburger menu
			mobileOpen && 'absolute inset-0 z-1',
		)}>
			<nav
				ref={navRef}
				className={clsx(
					'sticky top-0  overflow-y-auto scrollbar-thin',
					// height = screen - topbar + min(offset, topbar)
					'h-[calc(100vh-var(--topbar-height)+min(var(--nav-offset,0px),var(--topbar-height)))]',
				)}
			>
				<SidebarHeader className="hidden lg:flex">Track & Manage</SidebarHeader>

				<div className="flex flex-col">
					<SidebarButton
						text="Dashboard"
						icon={<DashboardOutlined />}
						selected={currentPath === '/dashboard'}
						to="/dashboard"
					/>

					<SidebarButton
						text="Reports"
						icon={<AssessmentOutlined />}
						selected={currentPath.startsWith('/reports')}
						to="/reports"
						onClick={resetReportsOverview}
						secondaryIcon={<IconButton size="s" icon={<Add />} className="invisible group-hover:visible text-component-navbar-text-desktop-primary_active" href="/reports" hrefState={{ new: true }} />}
					/>

					<Base.GlobalComponent
						name="numErrorsBalloon"
						content={() => (
							<SidebarButton
								text="Alarms"
								icon={<Icon path={mdiBellOutline} />}
								to="/alarms"
								selected={currentPath === '/alarms'}
								secondaryIcon={SystemData.genericData.numUnacknowledgedTriggerChecks > 0 ? (
									<AlertBalloon label={SystemData.genericData.numUnacknowledgedTriggerChecks} />
								) : null}
							/>
						)}
					/>

					{ productType === PRODUCT_TYPE.ADMIN && (
						<SidebarButton
							text="Publisher Accounts"
							icon={<PublisherIcon />}
							selected={currentPath.startsWith('/accounts')}
							to="/accounts"
						/>
					)}
					{ ((productType === PRODUCT_TYPE.PROGRAMMATIC ||  productType === PRODUCT_TYPE.FULL)
						&& !hide.advertiserMappings) && (
						<SidebarButton
							text="Advertiser Mappings"
							icon={<PersonIcon />}
							selected={currentPath.startsWith('/advertiser-mappings')}
							to="/advertiser-mappings"
						/>
					)}
				</div>

				<div className="border-t border-brand-800" />

				<SidebarHeader className="hidden lg:flex">Admin</SidebarHeader>
				<div>
					{ productType === PRODUCT_TYPE.ADMIN && hbmEnabled && (
						<SidebarButton
							text="HB Management"
							positionRef={navRef}
							floatingMenu={!maxSm}
							icon={<SettingsRemoteIcon />}
							expanded={expandedSections.includes(SECTION.HB_MANAGEMENT)}
							onExpandChange={() => updateExpandedSections(SECTION.HB_MANAGEMENT)}
							selected={adminHbManagementMenuItems.some(({ path }) => path === currentPath)}
						>
							{ adminHbManagementMenuItems.map(({ path, title }) => (
								<SidebarButton
									key={path}
									to={path}
									text={title}
									selected={path === currentPath}
								/>
							))}
						</SidebarButton>
					)}
					{ !hideInvoices && (
						<SidebarButton
							text="Invoices"
							icon={<ReceiptIcon />}
							to="/invoicing"
							selected={currentPath === '/invoicing'}
						/>
					)}
					{ productType === PRODUCT_TYPE.ADMIN && (
						<>
							<SidebarButton
								text="Configuration"
								floatingMenu={!maxSm}
								icon={<SettingsIcon />}
								expanded={expandedSections.includes(SECTION.CONFIGURATION)}
								onExpandChange={() => updateExpandedSections(SECTION.CONFIGURATION)}
								selected={adminSettingsMenuItems.some(({ path }) => path === currentPath) || currentPath === '/settings/prebid-builds'}
							>
								{ adminSettingsMenuItems.map(({ path, title }) => (
									<SidebarButton
										key={path}
										to={path}
										selected={path === currentPath}
										text={title}
									/>
								))}
								{ stores.identity.isSuperAdministrator() && (
									<SidebarButton
										to="/settings/prebid-builds"
										selected={currentPath === '/settings/prebid-builds'}
										text="Prebid builds"
									/>
								)}
							</SidebarButton>
							<SidebarButton
								text="Admin"
								floatingMenu={!maxSm}
								icon={<AdminPanelSettingsIcon />}
								expanded={expandedSections.includes(SECTION.ADMIN)}
								onExpandChange={() => updateExpandedSections(SECTION.ADMIN)}
								selected={currentPath === '/administrators' || adminMenuItems.some(({ path }) => path === currentPath) || currentPath === '/admin/instances'}
							>
								<SidebarButton
									to="/administrators"
									selected={currentPath === '/administrators'}
									text="Administrators"
								/>
								{ adminMenuItems.map(({ path, title }) => (
									<SidebarButton
										key={path}
										to={path}
										selected={path === currentPath}
										text={title}
									/>
								))}
								{ hasGlobalAdminAccess && (
									<SidebarButton
										to="/admin/instances"
										selected={currentPath === '/admin/instances'}
										text="Instances"
									/>
								)}
							</SidebarButton>
						</>
					)}
					{ (productType === PRODUCT_TYPE.PROGRAMMATIC || productType === PRODUCT_TYPE.FULL)
					&& canEditUsers && (
						<SidebarButton
							text="Users"
							icon={<PeopleIcon />}
							to="/users"
							selected={currentPath === '/users'}
						/>
					)}
				</div>

				{/* Favourites list for mobile and lg */}
				<div className="group sm:max-lg:hidden">
					<div className="border-t border-brand-800" />

					<SidebarHeader className="flex w-full pr-4!">
						<KidStarFilled className="size-4!" />
						<span className="text-nowrap">Favourites</span>
						<div className="ml-auto flex items-center gap-0">
							<Tooltip title={pinnedReportsSortOptions[pinnedReportsSort].tooltip}>
								<IconButton
									size="s"
									icon={<Icon path={pinnedReportsSortOptions[pinnedReportsSort].icon} />}
									className="invisible group-hover:visible text-white hover:text-white hover:bg-white/15 active:not-disabled:text-white data-active:text-white"
									onClick={handlePinnedSort}
								/>
							</Tooltip>
							<Tooltip title={favouritesCollapsed ? 'Expand favourites' : 'Collapse favourites'}>
								<IconButton
									size="s"
									icon={<Icon path={favouritesCollapsed ? mdiChevronDown : mdiChevronUp} />}
									className="text-white hover:text-white hover:bg-white/15 active:not-disabled:text-white data-active:text-white"
									onClick={() => setFavouritesCollapsed(!favouritesCollapsed)}
									aria-expanded={!favouritesCollapsed}
								/>
							</Tooltip>
						</div>
					</SidebarHeader>

					<Drawer open={!favouritesCollapsed} dismount>
						<List dense className="w-full">
							{pinnedReportsSorted?.map((report) => (
								<SidebarButton
									key={report.id}
									to={`${Constants[report.settings.type].reportLocation}/${report.id}`}
									text={report.settings.title}
									selected={(
										currentPath.includes(`${Constants[report.settings.type].reportLocation}/${report.id}`)
									)}
									className="py-2! **:truncate"
								/>
							))}
						</List>
					</Drawer>
				</div>

				{/* Custom favourites button for compact menu */}
				<div className="hidden sm:max-lg:block">
					<div className="border-t border-brand-800" />
					<SidebarButton
						text="Favourites"
						positionRef={navRef}
						floatingMenu={!maxSm}
						icon={<KidStarFilled />}
					>
						{ pinnedReportsSorted?.map(({ id, settings }) => (
							<SidebarButton
								key={id}
								to={`${Constants[settings.type].reportLocation}/${id}`}
								text={settings.title}
							/>
						))}
					</SidebarButton>
				</div>
			</nav>
		</div>
	);
}

function SidebarHeader({ className, children }: { className?: string, children: React.ReactNode }) {
	return (
		<Box className={clsx('flex h-[56px] items-center gap-2 px-5 pt-4 pb-2 text-xs font-semibold tracking-[1.44px] text-white uppercase', className)}>
			{children}
		</Box>
	);
}
